import { Permission } from '@/api/permissions';

/**
 * Plane-agnostic contract shapes for a role-administration board (GP-3853 Task
 * 11). The entity plane (`/entity/v1/roles`) and the Gamma plane
 * (`/user/v1/gamma`) implement the same shapes verbatim — `@/api/entity-roles/types`
 * and `@/api/gamma-roles/types` both re-export these rather than redeclaring
 * them, so the two planes cannot drift apart on the wire contract the shared
 * board components render.
 *
 * Plane-specific wrinkles stay in each plane's own `types.ts`.
 */

/**
 * Where a permission row on a role comes from.
 *
 * `base` rows are seeded and immutable — system roles carry them and the backend
 * rejects any write that touches one. `extra` rows are what an admin adds on top,
 * and are the only rows the API mutates. The column is CHECK-constrained to
 * these two values.
 */
export type RolePermissionSource = 'base' | 'extra';

export type CatalogPermission = {
  permission: Permission;
  /** Human label, e.g. "Publish NAV". Never derived on the frontend. */
  label: string;
  /** One-line "what this unblocks" copy shown under the label. */
  description: string;
  /**
   * Needs an explicit confirmation before it is granted — individually or via a
   * bulk group action.
   */
  sensitive: boolean;
};

export type CatalogGroup = {
  key: string;
  label: string;
  permissions: CatalogPermission[];
};

export type GetPermissionCatalogRes = {
  groups: CatalogGroup[];
};

export type RoleSummary = {
  id: string;
  name: string;
  isSystem: boolean;
  isLocked: boolean;
  systemKey: string | null;
  memberCount: number;
};

export type GetRolesRes = {
  roles: RoleSummary[];
};

export type RolePermission = {
  permission: Permission;
  source: RolePermissionSource;
};

export enum RoleScopeKind {
  AddressBookCategory = 'AddressBookCategory',
}

export type RoleScope = {
  kind: RoleScopeKind;
  resourceIds: string[];
};

export type GetRoleRes = RoleSummary & {
  permissions: RolePermission[];
  /** One entry per kind the role holds rows for; `[]` means it grants nothing of any kind. */
  scopes: RoleScope[];
};

export type SetRoleScopeBody = {
  resourceIds: string[];
};

/** One permission as a caller supplies it — `POST /roles`, `PATCH .../addPermissions`. */
export type PermissionAssignment = {
  permission: Permission;
};

export type CreateRoleBody = {
  name: string;
  permissions: PermissionAssignment[];
};

export type DuplicateRoleBody = {
  name: string;
};

export type UpdateRoleBody = {
  name?: string;
  addPermissions?: PermissionAssignment[];
  removePermissions?: Permission[];
};

/** ID reference only — the owning context never resolves name/email itself (that's
 * a cross-context call it must not make). The FE joins display data client-side
 * from whatever team/member list the surrounding page already fetches. */
export type RoleMemberRef = {
  userId: string;
};

export type GetRoleMembersRes = {
  members: RoleMemberRef[];
};

export type AddRoleMemberBody = {
  userId: string;
};

/** `POST /roles` and `POST /roles/:roleId/duplicate` — 201 with just the new id. */
export type CreateRoleRes = {
  id: string;
};
